import React, { useState, useRef, useMemo, ReactElement, useEffect, useCallback } from 'react';
import SearchInput from './SearchInput';
import Chips from 'Components/Chips';
import { CareProSearchDropdownPropType, CareProType } from './types';
import './style.css';

const CareProSearchDropdown = (props: CareProSearchDropdownPropType) => {
  const {
    dropdownDataPrimary,
    dropdownDataSecondary,
    label,
    placeholder,
    error,
    value,
    onChange,
    onChangeText,
    onChangeChip,
    onClick,
    disabled,
    required,
    onBlur,
    popupWidth,
  } = props;
  const inputRef: any = useRef<HTMLDivElement>(null);
  const popupStyle = useMemo(() => ({ width: popupWidth || 259 }), [popupWidth]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>("");

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (!inputRef.current.contains(event.target)) {
      setIsOpen(false);
      document.removeEventListener("mousedown", handleClickOutside);
      if (onBlur) {
        onBlur();
      }
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, handleClickOutside]);

  const _handleDropdownClick = () => {
    if (disabled) {
      return;
    }
    // TODO: on click when closing, remove focus from input
    setIsOpen(!isOpen);
  }

  const _handleOnInputTextChange = (inputText: string) => {
    setInputValue(inputText);
    onChangeText(inputText);
  }

  const _handleDropdownElementClick = (rowElement: CareProType) => {
    if (typeof rowElement === 'object' && rowElement !== null && _isCareProActive(rowElement.status)) {
      onChange(rowElement);
    }
  }

  const _onFieldClick = () => {
    if (disabled) {
      return;
    }
    onClick && onClick();
  }

  const _getDropdownPosition = (): string => {
    return window.innerHeight / 2 < inputRef.current.getBoundingClientRect().top
      ? "carepro-dropdown ms-upward"
      : "carepro-dropdown";
  };

  const _getDropdownHeight = (): number => {
    let height = 0;
    const clientRect = inputRef.current.getBoundingClientRect();
    if (window.innerHeight / 2 < clientRect.top) height = clientRect.top - 10;
    else height = window.innerHeight - clientRect.top - clientRect.height - 10;
    return Math.min(height, 385);
  };

  const _renderInput = (): ReactElement => {
    return <SearchInput
      label={""}
      placeholder={placeholder}
      error={error}
      value={inputValue}
      onChange={(inputText) => _handleOnInputTextChange(inputText)}
      disabled={disabled}
      required={required}
      isActive={isOpen}
      onClickInput={() => _handleDropdownClick()}
      onBlur={onBlur}
    />
  }

  const _isCareProActive = (status: string): boolean => {
    if (status === 'Approved') {
      return true;
    }
    return false;
  }

  const _renderCareProFullName = (firstName: string, lastName: string, status: string): ReactElement => {
    let fullName = `${firstName} ${lastName}`;

    if (!_isCareProActive(status)) {
      fullName = `${fullName} (Inactive)`;
    }

    return <div className={_isCareProActive(status) ? 'carepro-search-row-fullname' : 'carepro-search-row-fullname-inactive'}><span className="fs-14">{fullName}</span></div>
  }

  const _renderCareProTypeRaceAge = (cpType: string, race: string, age: string): ReactElement => {
    const hasCPType = cpType?.length > 0;
    const hasRace = race?.length > 0;
    const hasAge = age;
    return <div className='carepro-search-row-typeraceage'><span className="fs-14">{cpType}{hasCPType && (hasRace || hasAge) ? ', ' : ''}{race}{hasRace ? ', ' : ''}{age}{hasAge ? ' years old' : ''}</span></div>
  }

  const _renderCareProPhoneEmail = (phone: string, email: string): ReactElement => {
    return <div className='carepro-search-row-phoneemail'><span className="fs-14">{phone} | {email}</span></div>
  }

  const _renderPrimaryDropdownRows = (rowData: Array<CareProType> | undefined): Array<ReactElement> | null => {

    if (!rowData || rowData.length === 0) {
      return null;
    }

    const dropdownRows: Array<ReactElement> = [];

    dropdownRows.push(<div key={'completed-visits'} className='carepro-search-title-line'>
      <span>CPs with completed visits</span>
    </div>)

    const newRows: Array<ReactElement> = rowData
      .map((item: CareProType, index: number) => (
        <div
          key={`dropdownwithsearch-row-${index}`}
          className={
            `flex space-between ${_isCareProActive(item.status) ? 'carepro-dropdown-element-row' : 'carepro-dropdown-element-row-inactive'} 
            mt-0 carepro-item ${!disabled && "curson-pointer"}`
          }
          onClick={() => _handleDropdownElementClick(item)}
        >
          {_renderCareProFullName(item.first_name, item.last_name, item.status)}
          {_renderCareProTypeRaceAge(item.type, item.race, item.age)}
          {_renderCareProPhoneEmail(item.phone, item.email)}
        </div>
      ));

    dropdownRows.push(...newRows);

    return dropdownRows;
  };

  const _renderSecondaryDropdownRows = (rowData: Array<CareProType> | undefined): Array<ReactElement> | null => {

    if (!rowData || rowData.length === 0) {
      return null;
    }

    const dropdownRows: Array<ReactElement> = [];

    dropdownRows.push(<div key={'no-visits'} className='carepro-search-title-line'><span>CPs without completed visits</span></div>)

    const newRows: Array<ReactElement> = rowData
      .map((item: CareProType, index: number) => (
        <div
          key={`dropdownwithsearch-row-${index}`}
          className={
            `flex space-between ${_isCareProActive(item.status) ? 'carepro-dropdown-element-row' : 'carepro-dropdown-element-row-inactive'}
             mt-0 carepro-item ${!disabled && "curson-pointer"}`
          }
          onClick={() => _handleDropdownElementClick(item)}
        >
          {_renderCareProFullName(item.first_name, item.last_name, item.status)}
          {_renderCareProTypeRaceAge(item.type, item.race, item.age)}
          {_renderCareProPhoneEmail(item.phone, item.email)}
        </div>
      ));

    dropdownRows.push(...newRows);

    return dropdownRows;
  };

  const _renderDropdown = (): ReactElement | null => {
    if (isOpen) {
      return (
        <div className={_getDropdownPosition()} style={popupStyle}>
          <div>
            <div className="carepro-dropdown-wrapper" style={{ maxHeight: _getDropdownHeight() }}>
              {_renderPrimaryDropdownRows(dropdownDataPrimary)}
              {_renderSecondaryDropdownRows(dropdownDataSecondary)}
            </div>
          </div>
        </div>
      )
    }
    return null;
  }

  const _renderChips = (): ReactElement | null => {
    if (disabled || !value || value.length === 0) {
      return null;
    };

    const chips = value.map((carepro: CareProType) =>
      <Chips
        key={carepro.id}
        label={carepro.first_name + ' ' + carepro.last_name + (carepro.status === 'Approved' ? '' : ' (Inactive)')}
        active={carepro.status === 'Approved'}
        data={carepro}
        onClick={(data) => onChangeChip(data)}
        showInfoButton={!carepro.has_visited_cr_before}
        toolTipText={'Care Pro has not visited CR before. Please check if CP meets visit requirements.'}
      />)

    return <div className='carepro-chips-container'>{chips}</div>

  }

  return (<div
    ref={inputRef}
  >
    <div className="relative" onClick={() => _onFieldClick()}>
      {_renderInput()}
      {_renderDropdown()}
      {_renderChips()}
    </div>

  </div>);
}

export default CareProSearchDropdown;